import { useCallback, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useApiFetch } from '../api';
import type { ClientSummary } from '../types/models/Client';
import type { ApiResponseClientList } from '../types/api/ApiResponseClient';

interface UseClientsReturn {
  clients: ClientSummary[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Fetch the staff-only client picker list (GET /clients) — every client in
 * the system, INCLUDING the Ancalagon house client.
 *
 * NOT interchangeable with useImpersonationTargets: available-targets
 * deliberately excludes the caller's own client (you cannot impersonate
 * yourself), so it can never offer the house client. Use this hook wherever
 * the requirement is "any client, ours included" — e.g. dedicating a
 * certificate platform, assigning an orphan certificate.
 *
 * Backend rules to respect at call sites:
 * - Ancalagon staff only — mount/enable the hook behind a staff gate so the
 *   403-rejected call never fires for customer tenants.
 * - Rejected (403) while an impersonation session is active: an impersonating
 *   staff session operates at the impersonated tenant's scope. Surface the
 *   error with a hint to end the impersonation; retrying won't help.
 */
export const useClients = (enabled: boolean = true): UseClientsReturn => {
  const [clients, setClients] = useState<ClientSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const auth = useAuth();
  const apiFetch = useApiFetch();

  const fetchClients = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiFetch('/clients');
      const body = (await response.json().catch(() => null)) as ApiResponseClientList | null;

      if (body?.status === 'success' && body.data?.clients) {
        setClients(body.data.clients);
        return;
      }
      setError(body?.message ?? `Failed to load clients (HTTP ${`${response.status} ${response.statusText}`.trim()})`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while fetching the client list');
    } finally {
      setLoading(false);
    }
  }, [apiFetch]);

  useEffect(() => {
    if (enabled && auth.isAuthenticated) {
      void fetchClients();
    }
  }, [enabled, auth.isAuthenticated, fetchClients]);

  return { clients, loading, error, refetch: fetchClients };
};

export default useClients;
